import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState, StatCard, RoleBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Wallet as WalletIcon, Coins, Lock, CheckCircle } from "@phosphor-icons/react";

export default function Wallets() {
  const { user } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditFor, setCreditFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";
  const canCredit = isAdmin || user?.role === "reseller" || user?.role === "customer";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, me] = await Promise.all([
        api.get("/wallets"),
        isAdmin ? Promise.resolve({ data: null }) : api.get("/wallets/me"),
      ]);
      setWallets(w.data);
      setMine(me.data);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const credit = async () => {
    const minor = Math.round(parseFloat(amount) * 100);
    if (!minor || minor <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post(`/wallets/${creditFor.tenant_id}/credit`, {
        amount_minor: minor,
        idempotency_key: `credit-${creditFor.tenant_id}-${Date.now()}`,
      });
      toast.success(`Credited ${money(minor)} to ${creditFor.tenant_name}`);
      setCreditFor(null); setAmount("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title={user?.role === "customer" ? "My Wallet" : "Wallets"} subtitle="Dual-wallet balances · integer minor-units · USD" />

      {mine && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard testid="my-available" icon={WalletIcon} label="Available" value={money(mine.available_minor)} tone="success"
            hint="credits − holds − settlements" />
          <StatCard testid="my-holds" icon={Lock} label="Active Holds" value={money(mine.holds_minor)} tone="warning" />
          <StatCard testid="my-credits" icon={Coins} label="Total Credits" value={money(mine.credits_minor)} tone="primary" />
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : wallets.length === 0 ? (
        <EmptyState testid="wallets-empty" icon={WalletIcon} title="No wallets yet"
          description="Resellers and customers hold platform wallets. Create a tenant to provision its wallet."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="wallets-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Holds</TableHead>
                <TableHead className="text-right">Settled</TableHead>
                <TableHead className="text-right">Available</TableHead>
                {canCredit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {wallets.map((w) => (
                <TableRow key={w.id} className="hover:bg-muted/40" data-testid={`wallet-row-${w.tenant_id}`}>
                  <TableCell className="font-medium">{w.tenant_name || "—"}</TableCell>
                  <TableCell><RoleBadge role={w.owner_type} /></TableCell>
                  <TableCell className="text-right tabular-nums">{money(w.credits_minor)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[hsl(var(--warning))]">{money(w.holds_minor)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{money(w.settlements_minor)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{money(w.available_minor)}</TableCell>
                  {canCredit && (
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" className="rounded-lg"
                        onClick={() => setCreditFor(w)} data-testid={`credit-btn-${w.tenant_id}`}>Add funds</Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!creditFor} onOpenChange={(v) => !v && setCreditFor(null)}>
        <DialogContent data-testid="credit-dialog">
          <DialogHeader><DialogTitle>Add funds — {creditFor?.tenant_name}</DialogTitle>
            <DialogDescription>Idempotent, atomic credit in integer minor-units (USD).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Amount (USD)</Label>
            <Input data-testid="credit-amount" type="number" step="0.01" min="0"
              value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.00" />
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" weight="fill" /> Idempotent credit · atomic $inc
            </p>
          </div>
          <DialogFooter>
            <Button onClick={credit} disabled={saving} data-testid="submit-credit-btn" className="rounded-lg">
              {saving ? "Crediting…" : "Credit wallet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
